/**
 * Funções de data. Tudo trabalha com texto AAAA-MM-DD, sem hora e sem fuso,
 * para que uma venda nunca "mude de dia" por causa do horário do computador.
 *
 * Lê: nada.
 * Usado por: dominio/dias-uteis.ts, dominio/painel.ts, importacao/ e telas/.
 */
import type { DataIso, Mes } from './tipos.ts';

const PADRAO_DATA = /^(\d{4})-(\d{2})-(\d{2})$/;
const PADRAO_MES = /^(\d{4})-(\d{2})$/;

/** true se o texto é uma data real no formato AAAA-MM-DD (ex.: recusa 2026-02-30). */
export function ehDataIso(texto: string): boolean {
  const partes = PADRAO_DATA.exec(texto);
  if (!partes) return false;
  const [ano, mes, dia] = [Number(partes[1]), Number(partes[2]), Number(partes[3])];
  const data = new Date(Date.UTC(ano, mes - 1, dia));
  return data.getUTCFullYear() === ano && data.getUTCMonth() === mes - 1 && data.getUTCDate() === dia;
}

export function ehMes(texto: string): boolean {
  const partes = PADRAO_MES.exec(texto);
  return partes !== null && Number(partes[2]) >= 1 && Number(partes[2]) <= 12;
}

/** "2026-10-09" → "2026-10" */
export function mesDaData(data: DataIso): Mes {
  return data.slice(0, 7);
}

/** Monta AAAA-MM-DD a partir de ano, mês (1-12) e dia. */
export function montarData(ano: number, mes: number, dia: number): DataIso {
  return `${String(ano).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

/** Converte um instante (Date) para AAAA-MM-DD usando as partes em UTC. */
export function dataUtcParaIso(data: Date): DataIso {
  return montarData(data.getUTCFullYear(), data.getUTCMonth() + 1, data.getUTCDate());
}

/** Dia da semana: 0 = domingo ... 6 = sábado. */
export function diaDaSemana(data: DataIso): number {
  const [ano, mes, dia] = data.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(ano, mes - 1, dia)).getUTCDay();
}

/** Primeiro e último dia do mês. */
export function limitesDoMes(mes: Mes): { inicio: DataIso; fim: DataIso } {
  const [ano, numero] = mes.split('-').map(Number) as [number, number];
  const ultimoDia = new Date(Date.UTC(ano, numero, 0)).getUTCDate();
  return { inicio: montarData(ano, numero, 1), fim: montarData(ano, numero, ultimoDia) };
}

/** O dia seguinte: "2026-09-30" → "2026-10-01". */
export function diaSeguinte(data: DataIso): DataIso {
  const [ano, mes, dia] = data.split('-').map(Number) as [number, number, number];
  return dataUtcParaIso(new Date(Date.UTC(ano, mes - 1, dia + 1)));
}

/** Todas as datas de um período (as duas pontas incluídas), em ordem. */
export function datasDoPeriodo(inicio: DataIso, fim: DataIso): DataIso[] {
  const datas: DataIso[] = [];
  for (let data = inicio; data <= fim; data = diaSeguinte(data)) datas.push(data);
  return datas;
}

/** Todas as datas do mês, em ordem. */
export function datasDoMes(mes: Mes): DataIso[] {
  const [ano, numero] = mes.split('-').map(Number) as [number, number];
  const ultimoDia = new Date(Date.UTC(ano, numero, 0)).getUTCDate();
  return Array.from({ length: ultimoDia }, (_, i) => montarData(ano, numero, i + 1));
}

/** Soma (ou subtrai) meses: mesesDepois("2026-10", -1) → "2026-09". */
export function mesesDepois(mes: Mes, quantidade: number): Mes {
  const [ano, numero] = mes.split('-').map(Number) as [number, number];
  const data = new Date(Date.UTC(ano, numero - 1 + quantidade, 1));
  return montarData(data.getUTCFullYear(), data.getUTCMonth() + 1, 1).slice(0, 7);
}

/** Mês de hoje, pelo relógio do computador de quem está usando. */
export function mesAtual(agora: Date = new Date()): Mes {
  return montarData(agora.getFullYear(), agora.getMonth() + 1, 1).slice(0, 7);
}
